import _ from 'lodash';
import React from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {Icon} from '../../../../ui-toolkit/index';
import {FormGroup, Input, Table} from '../../../../ui-toolkit/indexExportV2';

import {FilterHeaderRow} from '../../../../ui-toolkit/elements/tableV2/features/FilterHeaderRow.tsx';
import {LabeledSuffix} from '../../../../ui-toolkit/form-v2/form-labels/FormLabeledSuffixPrefix';

/**
 * This is a specialized table row intended to contain the quick filters row of the entity table.
 */
export const FilterRow = styled(FilterHeaderRow)`
    th {
        ${FormGroup} {
            input,
            ${LabeledSuffix} {
                height: var(--height-form-control);
            }
        }
    }
`;

/**
 * This is a specialized table cell that contains an input field.
 * Notifies about value changes with an optional debounce.
 *
 * @param {object} param0
 * Props
 *
 * @param {(value: string) => void} param0.onChange
 * Callback for when the input value changes. Provides the input value as a parameter.
 * Updates are debounced, which can be configured via the `debounce` prop.
 *
 * @param {number} [param0.debounce]
 * The amount of milliseconds to debounce the onChange callback by.
 * Defaults to 1 second.
 *
 * @returns {JSX.Element}
 */
export const FilterRowCell = ({onChange, debounce}) => {
    const {t} = useTranslation();

    /**
     * This function invokes the onChange callback prop with the given value.
     * Note that this function is _debounced_, ie. invoking it doesn't immediately
     * invoke the onChange callback, but instead waits for the debounce time to pass.
     * You can force the callback to be invoked immediately by calling `submitChange.flush()`.
     */
    const submitChange = React.useMemo(
        () => _.debounce((value) => _.attempt(onChange, value), _.defaultTo(debounce, 1000)),
        [onChange, debounce],
    );

    /**
     * Handles changes to the input field value.
     * @param {React.ChangeEvent<HTMLInputElement>} ev
     */
    const handleChange = (ev) => {
        submitChange(ev.target.value);
    };

    /**
     * Handles key up events on the input field.
     * @param {React.KeyboardEvent<HTMLInputElement>} ev
     */
    const handleKeyUp = (ev) => {
        if (['Tab', 'Enter'].includes(ev.key)) {
            // if user presses tab/enter, flush the debounced callback,
            // ie. ignore debounce and submit right away
            submitChange.flush();
        }
    };

    return (
        <Table.Header>
            <Input
                raw
                type="text"
                placeholder={t('filterPanel.search')}
                suffix={<Icon type="sm" name="search" />}
                onChange={handleChange}
                onKeyUp={handleKeyUp}
            />
        </Table.Header>
    );
};

FilterRow.Cell = FilterRowCell;
